import { Injectable, signal } from '@angular/core';

import type { AcaoServico } from '@/features/servicos/shared/utils/servicos-acoes.utils';

/**
 * Chave de um protocolo dentro da memória de aceites.
 *
 * Recebe a chave do protocolo já pronta (`chaveProtocolo(item)`, do quadro) em vez de o `ItemStatus`
 * inteiro: assim este arquivo não importa `kanban-protocolos.ts`, que por sua vez importa este
 * serviço — o ciclo de import existiria de verdade, e é o tipo de coisa que só quebra em runtime.
 *
 * O `codigoServico` entra na chave porque `chaveProtocolo` é só `ano|numero`, e o mesmo par existe em
 * serviços diferentes. Sem ele, um protocolo 143/2026 preso no pré-médico faria o quadro de OUTRO
 * serviço recusar a finalização do SEU 143/2026 — um bloqueio falso sobre um ato que ainda não
 * aconteceu.
 *
 * <p>Rodada de correção 2 (Importante 1): a **UF** entra pelo mesmo motivo, um nível abaixo — a
 * numeração dos protocolos é por conselho regional, e o 143/2026 do CREMERJ não é o 143/2026 do
 * CREMESP. Ela ficou de fora na rodada anterior porque o lote resolvia a UF sem o fallback do quadro
 * e gravaria sob `''` enquanto o quadro relia sob `'RJ'`: chave presente nos dois lados, valor
 * diferente, proteção desligada em silêncio. Com o fallback corrigido
 * (`EncerramentoLoteService.ufDoItem`), os dois produtores resolvem a MESMA UF e a chave pode
 * completar-se. Quem chamar daqui é responsável por passar a UF já resolvida — nunca o `item.ufCrm`
 * cru, que é justamente o que pode vir vazio.</p>
 */
export function chaveDaConclusao(codigoServico: string, uf: string, chaveDoProtocolo: string): string {
    return `${codigoServico}|${uf}|${chaveDoProtocolo}`;
}

/**
 * O que o portal JÁ aceitou finalizar, por protocolo — a única memória desse fato no app.
 *
 * Existe porque a finalização no portal é irreversível e a gravação do movimento no quadro pode
 * falhar DEPOIS dela: enquanto o par não fecha, qualquer caminho que ofereça "efetivar/cancelar"
 * aquele protocolo de novo estaria oferecendo um SEGUNDO ato administrativo sobre o mesmo protocolo.
 *
 * <p>Um serviço, e não um sinal dentro de uma tela, porque o fato tem DOIS produtores — o fluxo
 * unitário do quadro e a fila do encerramento em lote — e um consumidor que precisa enxergar os dois:
 * o quadro. Enquanto a memória morou dentro do `KanbanProtocolos`, o lote a alimentava em lugar
 * nenhum, e um protocolo que ficasse preso no lote (portal aceitou, `mover` falhou) voltava ao quadro
 * sem marca alguma — pronto para ser efetivado outra vez pelo menu do card ou por um lote novo. Duas
 * memórias do mesmo fato foi exatamente o defeito que já custou uma correção nesta feature; a saída
 * não é sincronizá-las, é ter uma só.</p>
 *
 * <p>`providedIn: 'root'`, e não provido pelo dashboard como a fila: o fato é do PORTAL, não da tela
 * que por acaso o produziu. Um protocolo finalizado continua finalizado depois de o analista sair do
 * dashboard e voltar — e é justamente nessa volta que ele reencontra o card e tenta de novo. Escopo
 * de tela devolveria a memória zerada no momento em que ela mais importa.</p>
 *
 * <p>Entrada antiga nunca é perigosa: ela afirma que o portal já finalizou aquele protocolo, e isso
 * não deixa de ser verdade com o tempo. O que a apaga é o desfecho — o movimento gravado no quadro —,
 * nunca a passagem do tempo.</p>
 */
@Injectable({ providedIn: 'root' })
export class ConclusoesNoPortalService {
    private readonly aceitasSignal = signal<Map<string, AcaoServico>>(new Map());

    /**
     * O portal aceitou: registra ANTES de qualquer desvio de quem chamou. Vale inclusive para o ramo
     * em que a tela que pediu a finalização já sumiu — é o que impede que o mesmo protocolo seja
     * finalizado uma segunda vez por outro caminho.
     */
    registrar(chave: string, acao: AcaoServico): void {
        this.aceitasSignal.update((mapa) => new Map(mapa).set(chave, acao));
    }

    /**
     * O par fechou (portal finalizado E movimento gravado): a memória sai. Só daqui — antes disso ela
     * é a garantia contra a segunda finalização.
     */
    esquecer(chave: string): void {
        this.aceitasSignal.update((mapa) => {
            if (!mapa.has(chave)) {
                return mapa;
            }
            const restante = new Map(mapa);
            restante.delete(chave);
            return restante;
        });
    }

    /**
     * Qual ação o portal já aceitou para este protocolo, ou `null`. Lê o sinal por dentro, então
     * chamá-la de um `computed` mantém o `computed` reativo — quem pergunta não precisa saber que há
     * um sinal aqui.
     */
    acaoAceita(chave: string): AcaoServico | null {
        return this.aceitasSignal().get(chave) ?? null;
    }
}
